import { FormProvider, useForm } from 'react-hook-form'
import React, { useCallback, useContext, useState } from 'react'
import { StandardRegisterProps, State } from '.'
import {
  validationSchemaAccount,
  validationSchemaCompanyInformation,
  validationSchemaEmail,
  validationSchemaPassword,
  validationSchemaAdditionalUserRequest,
} from './validation'
import { useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import {
  MergeCartsById,
  MergeCartsByIdMutation,
  MergeCartsByIdMutationVariables,
  CreateActivation,
  CreateActivationMutationVariables,
  CreateActivationMutation,
  ActivationStatus,
} from 'src/graphql/generated/graphql-types'
import Error from './Error'
import DustinLogoSpinner from '@dustin-web/microsite-components/src/spinner'
import StepperProcess from '@dustin-web/microsite-components/src/stepper-process'
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import cookieHandler from '@dustin-web/auth-js/dist/handlers/cookieHandler'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import CustomRedirect from 'src/components/customRedirect'
import { accountCreated } from 'src/util/googleAnalyticsWrapperV2'
import AppContext from 'src/appContext'
import ValidationMessage from 'src/components/validationMessage'
import { PasswordRegisterProps } from 'src/components/createPassword/createAccountPasswordInputs'

export const useYupValidationResolver = (state: State) =>
  useCallback(
    async (data: StandardRegisterProps) => {
      try {
        let schema = schemas[State[state]]
        if (data.additionalUserRequest) {
          schema = schemas.additionalUserRequest
        }


        const values = await schema.validate(data, {
          abortEarly: false,
        })

        return {
          values,
          errors: {},
        }
      } catch (errors: any) {
        return {
          values: {},
          errors: errors.inner.reduce(
            (allErrors: any, currentError: any) => ({
              ...allErrors,
              [currentError.path]: {
                type: currentError.type ?? 'validation',
                message: currentError.message,
              },
            }),
            {}
          ),
        }
      }
    },
    [state]
  )

export const useYupValidationPasswordResolver = () =>
  useCallback(async (data: PasswordRegisterProps) => {
    try {
      const schema = validationSchemaPassword

      const values = await schema.validate(data, {
        abortEarly: false,
      })

      return {
        values,
        errors: {},
      }
    } catch (errors: any) {
      return {
        values: {},
        errors: errors.inner.reduce(
          (allErrors: any, currentError: any) => ({
            ...allErrors,
            [currentError.path]: {
              type: currentError.type ?? 'validation',
              message: currentError.message,
            },
          }),
          {}
        ),
      }
    }
  }, [])

const schemas: any = {
  email: validationSchemaEmail,
  companyInfo: validationSchemaCompanyInformation,
  account: validationSchemaAccount,
  password: validationSchemaPassword,
  additionalUserRequest: validationSchemaAdditionalUserRequest,
}

type FormProps = {
  defaultValues: StandardRegisterProps
  state: State
  returnUrl: string | null
  onStateChange: (state: State) => void
  children: React.ReactNode
  showStepper?: boolean
}

const Form: React.FC<FormProps> = ({
  defaultValues,
  state,
  returnUrl,
  onStateChange,
  children,
  showStepper
}) => {
  const [errorRegisteringContact, setErrorRegisteringContact] = useState<boolean>(false)
  const { setCurrentContact } = useAuth()
  const { sourcingCompany } = useContext(AppContext)
  const { translate } = useTranslations()
  const cookies = cookieHandler()
  const [status, setStatus] = useState<ActivationStatus | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [redirectLink, setRedirectLink] = useState<string | null>(null)
  const { isSmallScreen } = useResponsive()

  const methods = useForm<StandardRegisterProps>({
    resolver: useYupValidationResolver(state),
    defaultValues,
  })

  const [mergeCartsById] = useGraphqlMutation<
    MergeCartsByIdMutation,
    MergeCartsByIdMutationVariables
  >(MergeCartsById)

  const [mutation] = useGraphqlMutation<
    CreateActivationMutation,
    CreateActivationMutationVariables
  >(CreateActivation)

  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.code === 'Enter') e.preventDefault()
  }

  const onSubmit = async ({
    acceptAccountTerms,
    companyName,
    email,
    firstName,
    lastName,
    offersByEmail,
    organizationNumber,
    preRegistrationToken
  }: StandardRegisterProps) => {
    if (state !== State.account) {
      onStateChange(state + 1)
    } else {
      setErrorRegisteringContact(false)
      setLoading(true)

      const result: CreateActivationMutation = await mutation({
        variables: {
          accountTermsAccepted: acceptAccountTerms,
          companyName,
          firstName,
          lastName,
          offersByEmail,
          email: email == '' ? defaultValues.email : email,
          orgNumber: organizationNumber,
          preRegistrationToken,
        },
      })

      if (!result) {
        setErrorRegisteringContact(true)
      }

      if (
        result?.createActivation?.status === ActivationStatus.Success &&
        result?.createActivation?.activationId
      ) {
        const { cartId, isAuthenticated } = defaultValues

        accountCreated(sourcingCompany)

        if (isAuthenticated) {
          try {
            if (!!cartId) {
              await mergeCartsById({
                variables: {
                  sourceCartId: cartId,
                  targetContactId: result.createActivation.activationId,
                },
              })
            }
          } catch (error) {
            console.error('Failed to merge cart', {
              error,
              contactId: result.createActivation.activationId,
            })
          }

          await setCurrentContact(result.createActivation.activationId)
          setRedirectLink(returnUrl ?? '/')
        } else {
          cookies.setContactId(result.createActivation.activationId)
          onStateChange(State.emailVerification)
        }
      } else {
        setErrorRegisteringContact(true)
      }

      setStatus(result?.createActivation?.status ?? null)
      setLoading(false)
    }
  }

  if (redirectLink) {
    return <CustomRedirect url={redirectLink} />
  }

  return (
    <FormProvider {...methods}>
      {loading && <DustinLogoSpinner dustinSpinner />}

      {!isSmallScreen && showStepper && (
        <StepperProcess
          labels={[
            translate('Register_email'),
            translate('REGISTRATION_COMPANY_INFORMATION'),
            translate('RegisterBuinsess_Account_Information'),
            translate('Register_EmailVerification')
          ]}
          currentIdx={state}
          bgCompletedClassName={'u-bg-dark-gray'}
          activeStepClassName="u-color-dark-gray u-fw-bold"
        />
      )}

      <div className={`${state !== 7 ? 'u-pt-16 u-pt-lg-40' : ''}`}>
        <form onSubmit={methods.handleSubmit(onSubmit)} onKeyDown={checkKeyDown}>
          {children}
        </form>
      </div>

      {errorRegisteringContact && (
        <div className="row">
          <div className="col-12">
            <ValidationMessage>{translate('REGISTRATION_UNKNOWN_ERROR')}</ValidationMessage>
          </div>
        </div>
      )}

      <Error email={methods.getValues('email')} returnUrl={returnUrl} status={status} />
    </FormProvider>
  )
}

export default Form
